import { createMutation, QueryClient, useQueryClient } from "@tanstack/svelte-query";
import type { Collection, Mimir } from "bildebanken-model";
import {
  deleteCollection,
  updateCollection,
  ConflictError,
  createCollection,
  removeUserFromCollection,
} from "services/collections/api";
import { notify } from "services/notifications";
import { get } from "svelte/store";
import { productFruitsInstance } from "services/productFruitsService";
import { escapeHtml } from "utils/fns";

interface CacheUpdateOptions {
  queryKey: string[];
  exact?: boolean;
}

//remove uuidv4 from collection name. uuidv4 is added to ensure unique names in Mimir, see post /collections in v1/routes.ts
const uuidSuffix = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const displayName = (collection: Collection) =>
  collection.metadata.displayName ?? collection.name.replace(uuidSuffix, "");
/**
 * Update collections cache by modifying a specific collection
 */
function updateCollectionInCache(
  queryClient: QueryClient,
  collectionId: string,
  update: (collection: Collection) => Collection,
  options: CacheUpdateOptions = { queryKey: ["collections"], exact: false },
) {
  queryClient.setQueriesData(options, (oldData: { pages: Collection[][] } | undefined) => {
    if (!oldData?.pages) {
      return oldData;
    }

    return {
      ...oldData,
      pages: oldData.pages.map((page: Collection[]) =>
        page.map((collection: Collection) =>
          collection.id === collectionId ? update(collection) : collection,
        ),
      ),
    };
  });
}

/**
 * Remove a collection from cache
 */
function removeCollectionFromCache(
  queryClient: QueryClient,
  collectionId: string,
  options: CacheUpdateOptions = { queryKey: ["collections"], exact: false },
) {
  queryClient.setQueriesData(options, (oldData: { pages: Collection[][] } | undefined) => {
    if (!oldData?.pages) {
      return oldData;
    }

    return {
      ...oldData,
      pages: oldData.pages.map((page: Collection[]) =>
        page.filter((collection: Collection) => collection.id !== collectionId),
      ),
    };
  });
}

/**
 * Add a collection to the top of cache
 */
function prependCollectionToCache(
  queryClient: QueryClient,
  collection: Collection,
  options: CacheUpdateOptions = { queryKey: ["collections"], exact: false },
) {
  queryClient.setQueriesData(options, (oldData: { pages: Collection[][] } | undefined) => {
    if (!oldData?.pages) {
      return oldData;
    }

    return {
      ...oldData,
      pages: [[collection, ...(oldData.pages[0] ?? [])], ...oldData.pages.slice(1)],
    };
  });
}

export function useUpdateCollectionMutation() {
  const queryClient = useQueryClient();

  return createMutation({
    mutationFn: async ({ collectionId, newName }: { collectionId: string; newName: string }) => {
      await updateCollection(collectionId, { name: newName });
    },
    onSuccess: (_data, variables) => {
      const { collectionId, newName } = variables;
      updateCollectionInCache(queryClient, collectionId, (collection) => ({
        ...collection,
        name: newName,
        metadata: { ...collection.metadata, displayName: newName },
      }));
      get(productFruitsInstance)?.api.events.track("collection-edited");
    },
    onError: (error, variables) => {
      if (error instanceof ConflictError) {
        throw error; // Let component handle conflict state
      }
      notify(`Kunne ikke endre samling til "${escapeHtml(variables.newName)}"`, "error", 5000);
    },
  });
}

export function useDeleteCollectionMutation() {
  const queryClient = useQueryClient();

  return createMutation({
    mutationFn: async (collectionId: string) => {
      await deleteCollection(collectionId);
    },
    onSuccess: (_data, collectionId) => {
      removeCollectionFromCache(queryClient, collectionId);
      get(productFruitsInstance)?.api.events.track("collection-deleted");
    },
    onError: () => {
      notify("Kunne ikke slette samling", "error", 5000);
    },
  });
}

export function useCreateCollectionMutation(onCreatedCallback?: (collection: Collection) => void) {
  const queryClient = useQueryClient();

  return createMutation({
    mutationFn: async ({ name, visibleTo }: { name: string; visibleTo: Mimir.AssetVisibility }) => {
      return await createCollection(name, visibleTo);
    },
    onSuccess: async (collection) => {
      onCreatedCallback?.(collection);
      await queryClient.invalidateQueries({ queryKey: ["collections"], exact: false });
      get(productFruitsInstance)?.api.events.track("collection-created");
    },
    onError: (error, variables) => {
      if (error instanceof ConflictError) {
        throw error; // Let component handle conflict state
      }
      notify(`Kunne ikke opprette samlingen "${escapeHtml(variables.name)}"`, "error", 5000);
    },
  });
}

export async function removeUserFromCollectionAndUpdateCache(
  queryClient: QueryClient,
  collectionId: string,
  userId: string,
) {
  try {
    const updatedVisibleTo = await removeUserFromCollection(collectionId, userId);
    if (!updatedVisibleTo) {
      notify("Kunne ikke fjerne bruker fra samlingen", "error", 5000);
      return;
    }

    updateCollectionInCache(queryClient, collectionId, (collection) => ({
      ...collection,
      visibleTo: updatedVisibleTo,
    }));
    notify("Bruker fjernet fra samlingen", "success");
  } catch (error) {
    console.error("Error removing user from collection:", error);
    notify("Kunne ikke fjerne bruker fra samlingen", "error", 5000);
  }
}

export { updateCollectionInCache, removeCollectionFromCache, prependCollectionToCache };
